import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of, map, switchMap, catchError } from 'rxjs';
import { ServicioContenidoModyo } from '../modyo/modyo-contenido.service';
import type {
  ModyoEntry,
  ModyoReference,
  ReferenciaNoExpandida,
  ResultadoExpansionReferencia,
} from '@core/interfaces/modyo';

const PROFUNDIDAD_MAXIMA_POR_DEFECTO = 10;
const PROFUNDIDAD_INICIAL = 0;
const INCREMENTO_PROFUNDIDAD = 1;

const CACHE_TTL_MINUTOS = 5;
const CACHE_TTL_SEGUNDOS_POR_MINUTO = 60;
const CACHE_TTL_MS_POR_SEGUNDO = 1000;
const CACHE_TTL_MS = CACHE_TTL_MINUTOS * CACHE_TTL_SEGUNDOS_POR_MINUTO * CACHE_TTL_MS_POR_SEGUNDO;
const CACHE_TAMANO_MAXIMO = 100;
const CACHE_PORCENTAJE_EVICCION = 0.2;

interface EntradaCache {
  entry: ModyoEntry;
  timestampCreacion: number;
  timestampUltimoAcceso: number;
}

@Injectable({
  providedIn: 'root',
})
export class ServicioExpansionReferenciasModyo {
  private readonly servicioModyo = inject(ServicioContenidoModyo);

  private readonly cacheReferencias = new Map<string, EntradaCache>();

  public esReferencia(valor: unknown): valor is ModyoReference {
    const esObjetoValido = typeof valor === 'object' && valor !== null;
    if (!esObjetoValido) {
      return false;
    }

    const tienePropiedadesReferencia = 'uuid' in valor && 'content_type' in valor;
    if (!tienePropiedadesReferencia) {
      return false;
    }

    const yaEstaExpandida = 'fields' in valor || 'meta' in valor;
    if (yaEstaExpandida) {
      return false;
    }

    const contentType = (valor as { content_type: string }).content_type;
    const esTipoMime = contentType.includes('/');
    if (esTipoMime) {
      return false;
    }

    return true;
  }

  public esEntryExpandida(valor: unknown): valor is ModyoEntry {
    return typeof valor === 'object' && valor !== null && 'meta' in valor && 'fields' in valor;
  }

  public expandirEntry<T = unknown>(
    entry: ModyoEntry<T>,
    maxProfundidad = PROFUNDIDAD_MAXIMA_POR_DEFECTO,
    profundidadActual = PROFUNDIDAD_INICIAL,
  ): Observable<ModyoEntry<T>> {
    const esEntryValida = entry && typeof entry === 'object';
    if (!esEntryValida) {
      return of({} as ModyoEntry<T>);
    }

    const tieneFields = 'fields' in entry && entry.fields;
    if (!tieneFields) {
      return of(entry);
    }

    const alcanzoProfundidadMaxima = profundidadActual >= maxProfundidad;
    if (alcanzoProfundidadMaxima) {
      return of(entry);
    }

    return this.expandirObjeto(entry.fields, profundidadActual + INCREMENTO_PROFUNDIDAD, maxProfundidad).pipe(
      map((fieldsExpandidos) => ({
        ...entry,
        fields: fieldsExpandidos as T,
      })),
    );
  }

  public expandirReferenciasEnLote(
    referencias: ModyoReference[],
    maxProfundidad = PROFUNDIDAD_MAXIMA_POR_DEFECTO,
  ): Observable<ResultadoExpansionReferencia[]> {
    const noHayReferencias = referencias.length === PROFUNDIDAD_INICIAL;
    if (noHayReferencias) {
      return of([]);
    }

    const observables = referencias.map((ref) =>
      this.expandirReferenciaUnica(ref, PROFUNDIDAD_INICIAL, maxProfundidad),
    );

    return forkJoin(observables);
  }

  public contarReferencias(obj: unknown): number {
    let count = 0;

    const contar = (valor: unknown): void => {
      if (this.esReferencia(valor)) {
        count++;
      } else if (Array.isArray(valor)) {
        valor.forEach(contar);
      } else if (valor && typeof valor === 'object') {
        Object.values(valor).forEach(contar);
      }
    };

    contar(obj);
    return count;
  }

  public obtenerReferencias(obj: unknown): ModyoReference[] {
    const referencias: ModyoReference[] = [];

    const buscar = (valor: unknown): void => {
      if (this.esReferencia(valor)) {
        referencias.push(valor);
      } else if (Array.isArray(valor)) {
        valor.forEach(buscar);
      } else if (valor && typeof valor === 'object') {
        Object.values(valor).forEach(buscar);
      }
    };

    buscar(obj);
    return referencias;
  }

  public limpiarCache(): void {
    this.cacheReferencias.clear();
  }

  public limpiarEntradasExpiradas(): number {
    const ahora = Date.now();
    let entradasEliminadas = 0;

    for (const [clave, entradaCache] of this.cacheReferencias.entries()) {
      const tiempoTranscurrido = ahora - entradaCache.timestampCreacion;
      const entradaExpirada = tiempoTranscurrido > CACHE_TTL_MS;

      if (entradaExpirada) {
        this.cacheReferencias.delete(clave);
        entradasEliminadas++;
      }
    }

    return entradasEliminadas;
  }

  public obtenerEstadisticasCache(): {
    tamano: number;
    tamanoMaximo: number;
    ttlMs: number;
    claves: string[];
    entradasConTiempo: Array<{ clave: string; edadMs: number; ultimoAccesoMs: number }>;
  } {
    const ahora = Date.now();
    const entradasConTiempo = Array.from(this.cacheReferencias.entries()).map(([clave, entrada]) => ({
      clave,
      edadMs: ahora - entrada.timestampCreacion,
      ultimoAccesoMs: ahora - entrada.timestampUltimoAcceso,
    }));

    return {
      tamano: this.cacheReferencias.size,
      tamanoMaximo: CACHE_TAMANO_MAXIMO,
      ttlMs: CACHE_TTL_MS,
      claves: Array.from(this.cacheReferencias.keys()),
      entradasConTiempo,
    };
  }

  public precargarReferencias(referencias: ModyoReference[]): Observable<void> {
    const noHayReferencias = referencias.length === PROFUNDIDAD_INICIAL;
    if (noHayReferencias) {
      return of(undefined);
    }

    return this.expandirReferenciasEnLote(referencias).pipe(map((): void => undefined));
  }

  public esReferenciaNoExpandida(valor: unknown): valor is ReferenciaNoExpandida {
    const esObjetoValido = typeof valor === 'object' && valor !== null;
    if (!esObjetoValido) {
      return false;
    }

    return 'expansionFallida' in valor && (valor as ReferenciaNoExpandida).expansionFallida === true;
  }

  private expandirObjeto(obj: unknown, profundidadActual: number, maxProfundidad: number): Observable<unknown> {
    if (this.esReferencia(obj)) {
      return this.expandirReferenciaUnica(obj, profundidadActual, maxProfundidad);
    }

    if (this.esEntryExpandida(obj)) {
      return this.expandirEntry(obj, maxProfundidad, profundidadActual);
    }

    if (Array.isArray(obj)) {
      return this.expandirArray(obj, profundidadActual, maxProfundidad);
    }

    if (obj && typeof obj === 'object') {
      return this.expandirObjetoPlano(obj as Record<string, unknown>, profundidadActual, maxProfundidad);
    }

    return of(obj);
  }

  private crearReferenciaNoExpandida(
    ref: ModyoReference,
    razonFallo: 'no_encontrada' | 'error_servicio',
    mensajeError?: string,
  ): ReferenciaNoExpandida {
    return {
      uuid: ref.uuid,
      content_type: ref.content_type,
      expansionFallida: true,
      razonFallo,
      mensajeError,
    };
  }

  private expandirReferenciaUnica(
    ref: ModyoReference,
    profundidadActual: number,
    maxProfundidad: number,
  ): Observable<ResultadoExpansionReferencia> {
    const cacheKey = `${ref.content_type}:${ref.uuid}`;

    this.limpiarEntradasExpiradas();

    const entradaCacheada = this.obtenerDeCache(cacheKey);
    if (entradaCacheada) {
      return of(entradaCacheada);
    }

    return this.servicioModyo.obtenerEntradaPorUuid<ModyoEntry>(ref.content_type, ref.uuid).pipe(
      switchMap((entry) => {
        const entryNoEncontrada = !entry;
        if (entryNoEncontrada) {
          const referenciaFallida = this.crearReferenciaNoExpandida(ref, 'no_encontrada');
          return of(referenciaFallida);
        }

        this.guardarEnCache(cacheKey, entry);
        return this.expandirEntry(entry, maxProfundidad, profundidadActual);
      }),
      map((entryExpandida) => {
        const esEntryValidaParaCache =
          entryExpandida &&
          'meta' in entryExpandida &&
          'fields' in entryExpandida &&
          !this.esReferenciaNoExpandida(entryExpandida);

        if (esEntryValidaParaCache) {
          this.guardarEnCache(cacheKey, entryExpandida);
        }

        return entryExpandida;
      }),
      catchError((error) => {
        const mensajeError = error instanceof Error ? error.message : String(error);
        const referenciaFallida = this.crearReferenciaNoExpandida(ref, 'error_servicio', mensajeError);
        return of(referenciaFallida);
      }),
    );
  }

  private expandirArray(arr: unknown[], profundidadActual: number, maxProfundidad: number): Observable<unknown[]> {
    const arrayVacio = arr.length === PROFUNDIDAD_INICIAL;
    if (arrayVacio) {
      return of([]);
    }

    const observables = arr.map((elemento) => this.expandirObjeto(elemento, profundidadActual, maxProfundidad));
    return forkJoin(observables);
  }

  private expandirObjetoPlano(
    obj: Record<string, unknown>,
    profundidadActual: number,
    maxProfundidad: number,
  ): Observable<Record<string, unknown>> {
    const keys = Object.keys(obj);
    const objetoVacio = keys.length === PROFUNDIDAD_INICIAL;

    if (objetoVacio) {
      return of(obj);
    }

    const observables: Record<string, Observable<unknown>> = {};

    keys.forEach((key) => {
      observables[key] = this.expandirObjeto(obj[key], profundidadActual, maxProfundidad);
    });

    return forkJoin(observables);
  }

  private obtenerDeCache(cacheKey: string): ModyoEntry | null {
    const entradaCache = this.cacheReferencias.get(cacheKey);
    if (!entradaCache) {
      return null;
    }

    const ahora = Date.now();
    const tiempoTranscurrido = ahora - entradaCache.timestampCreacion;
    const entradaExpirada = tiempoTranscurrido > CACHE_TTL_MS;

    if (entradaExpirada) {
      this.cacheReferencias.delete(cacheKey);
      return null;
    }

    entradaCache.timestampUltimoAcceso = ahora;

    return entradaCache.entry;
  }

  private guardarEnCache(cacheKey: string, entry: ModyoEntry): void {
    this.evictarSiNecesario();

    const ahora = Date.now();
    const entradaExistente = this.cacheReferencias.get(cacheKey);

    if (entradaExistente) {
      entradaExistente.entry = entry;
      entradaExistente.timestampUltimoAcceso = ahora;
    } else {
      this.cacheReferencias.set(cacheKey, {
        entry,
        timestampCreacion: ahora,
        timestampUltimoAcceso: ahora,
      });
    }
  }

  private evictarSiNecesario(): void {
    const cacheAlcanzoLimite = this.cacheReferencias.size >= CACHE_TAMANO_MAXIMO;
    if (!cacheAlcanzoLimite) {
      return;
    }

    const cantidadAEvictar = Math.ceil(CACHE_TAMANO_MAXIMO * CACHE_PORCENTAJE_EVICCION);
    const entradasOrdenadas = Array.from(this.cacheReferencias.entries()).sort(
      ([, a], [, b]) => a.timestampUltimoAcceso - b.timestampUltimoAcceso,
    );

    for (let i = 0; i < cantidadAEvictar && i < entradasOrdenadas.length; i++) {
      const [clave] = entradasOrdenadas[i];
      this.cacheReferencias.delete(clave);
    }
  }
}
